import { Consumer, ContactInput } from '@toki-power/public-api';
import logger from 'logger';

import { MeteringPoint, PartiallyCompletedOnboardingRequest } from '~/services/firestore';
import { Representative } from '~/services/firestore/onboarding/types';

import { buildConsumerSettlement } from '../buildConsumerSettlement/buildConsumerSettlement';
import { buildPhone } from '../buildPhone/buildPhone';

interface Props {
  data: PartiallyCompletedOnboardingRequest;
}

const buildMeteringPoints = (meteringPointsData: MeteringPoint[]): Consumer['meteringPoints'] =>
  meteringPointsData.map((meteringPoint) => {
    const { address } = meteringPoint;

    const meteringPointAddress = Object.values(address)
      .filter((value) => value !== '')
      .join(', ');

    return {
      address: meteringPointAddress,
      id: meteringPoint.meteringPointId,
      name: meteringPoint.name,
      previousUtility: meteringPoint.previousUtility ? meteringPoint.previousUtility : '',
    };
  });

const buildCustomerName = (customerData: PartiallyCompletedOnboardingRequest['customerData']): string =>
  [customerData.firstName, customerData.middleName, customerData.lastName].filter(Boolean).join(' ');

const buildCustomerContactName = (customerData: PartiallyCompletedOnboardingRequest['customerData']): string => {
  if (customerData.customerType === 'person') {
    return buildCustomerName(customerData);
  }

  const representative = customerData.representatives[0];
  const representativeName = [representative.firstName, representative.middleName, representative.lastName]
    .filter(Boolean)
    .join(' ');

  return representativeName;
};

const buildBillingContact = (
  billingContact: PartiallyCompletedOnboardingRequest['customerData']['billingContact']
): ContactInput | null => {
  const { email, firstName, lastName, phone } = billingContact;

  if (!firstName && !lastName) {
    return null;
  }

  return {
    email: email || '',
    name: [firstName, lastName].filter(Boolean).join(' '),
    phoneMobile: phone.value ? buildPhone(phone) : '',
    type: 'billing',
  };
};

const buildContacts = (
  contactName: string,
  contact: PartiallyCompletedOnboardingRequest['accountData'],
  billingContact: PartiallyCompletedOnboardingRequest['customerData']['billingContact']
): ContactInput[] => {
  const { email, phoneNumber } = contact;

  const contacts: ContactInput[] = [
    {
      email,
      name: contactName,
      phoneMobile: phoneNumber.value ? buildPhone(phoneNumber) : '',
      type: 'contact',
    },
  ];

  const billingContactFormatted = buildBillingContact(billingContact);

  if (billingContactFormatted && billingContactFormatted.email !== email) {
    contacts.push(billingContactFormatted);
  }

  return contacts;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/* eslint-disable sort-keys-fix/sort-keys-fix */
export const buildFullName = ({
  firstName,
  middleName,
  lastName,
}: {
  firstName: string;
  middleName: string;
  lastName: string;
}): string => {
  const nameData = {
    // The order of the keys in the object will determine the rendering order
    firstName,
    middleName,
    lastName,
  };

  const name = Object.entries(nameData).reduce((accumulator, [_, value]) => {
    if (!accumulator.length) {
      // eslint-disable-next-line no-param-reassign
      accumulator = value;
      return accumulator;
    }

    // eslint-disable-next-line no-param-reassign
    accumulator = accumulator.concat(' ', value);
    return accumulator;
  }, '');
  return name;
};

export const buildRepresentativesList = (names: Representative[] | string) =>
  typeof names === 'string'
    ? [{ name: names }]
    : names?.map((owner) => ({
        name: buildFullName({ firstName: owner.firstName, lastName: owner.lastName, middleName: owner.middleName }),
      }));

export const buildRepresentatives = (names: Representative[] | string) =>
  buildRepresentativesList(names)
    .map(({ name }) => name)
    .join(' , ');

export const prepareOnboardingData = ({ data }: Props): Consumer => {
  const { customerData, meteringPointsData, accountData } = data;

  if (!customerData.customerType) {
    logger.error('Customer type is required');
    throw new Error('Customer type is required');
  }

  const customerName = buildCustomerName(customerData);
  const customerContactName = buildCustomerContactName(customerData);
  const meteringPoints = buildMeteringPoints(meteringPointsData);

  const contacts = buildContacts(customerContactName, accountData, customerData.billingContact);

  return {
    accountManagerId: customerData.accountManager,
    address: {
      address: customerData.address.addressLine1,
      area: customerData.address.area || '',
      postCode: customerData.address.postCode || '',
      region: customerData.address.region || '',
      settlement: customerData.address.settlement ? buildConsumerSettlement(customerData.address.settlement) : '',
    },
    contacts,
    customer: {
      id: customerData.customerType === 'person' ? customerData.personalId : customerData.eik,
      name: customerData.customerType === 'person' ? customerName : customerData.legalEntityName,
      type: customerData.customerType,
      representatives:
        customerData.customerType === 'person' ? customerName : buildRepresentatives(customerData.representatives),
    },
    meteringPoints,
    planId: customerData.planId,
  };
};
